import React, { useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';

import Badge from 'components/Badge';
import Message from 'components/Message';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { schedulePropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';

import SegmentedCapacityBar, { SEGMENTED_CAPACITY_BAR_SEGMENT } from '../Components/SegmentedCapacityBar';
import {
  SCHEDULE_LIST_V2_ADD_SPOT_TYPE,
  SCHEDULE_LIST_V2_FILL_BAR_VARIANT,
  SCHEDULE_LIST_V2_STATUS_BADGE,
  getScheduleListV2AdminBadgeMessage,
  getScheduleListV2FillBar,
  getScheduleListV2StatusRow,
  isScheduleListV2InThePast,
} from './ScheduleListV2.helpers';
import { messages } from './ScheduleListV2Item.messages';

import './ScheduleListV2Item.scss';

const { element } = bem('schedule-list-v2-item');
const FILL_BAR_TOOLTIP_NAME_PREFIX = 'schedule-list-v2-fill-bar';
const STATUS_BADGE_TOOLTIP_NAME_PREFIX = 'schedule-list-v2-status-badge';

const STATUS_BADGE_CONFIG = {
  [SCHEDULE_LIST_V2_STATUS_BADGE.NEW_CLIENTS]: {
    icon: ICONS.userAdd,
    labelMessage: messages.badgeNew,
    purpleSoft: true,
    tooltipMessage: messages.badgeNewTooltip,
  },
  [SCHEDULE_LIST_V2_STATUS_BADGE.SMARTSPOT_OFF]: {
    icon: ICONS.alertSmall,
    labelMessage: messages.badgeSmartspotOff,
    warningSoft: true,
  },
  [SCHEDULE_LIST_V2_STATUS_BADGE.WAITING]: {
    icon: ICONS.bellNotificationSmall,
    labelMessage: messages.badgeWaiting,
    purpleSoft: true,
    tooltipMessage: messages.badgeWaitingTooltip,
  },
};

function FillBarClasspassTooltip({ name, show }) {
  if (!show) {
    return null;
  }

  return (
    <>
      <TooltipToggle
        className={element('fill-bar-tooltip-toggle')}
        name={name}
        onMouseDown={event => {
          event.stopPropagation();
        }}>
        <Isvg src={ICONS.infoGreyLight} />
      </TooltipToggle>
      <TooltipContent hoverable name={name} place="top">
        <Message message={messages.classpassFullTooltip} />
      </TooltipContent>
    </>
  );
}

FillBarClasspassTooltip.propTypes = {
  name: PropTypes.string.isRequired,
  show: PropTypes.bool,
};

function getFillBarLegendItems({ fillBarLabelValues, isIntegratedFillBar, tooltip }) {
  const classpassCount = (
    <span className={element('fill-bar-count')}>
      <Message message={messages.fillBarCount} values={fillBarLabelValues} />
    </span>
  );
  const classpassItem = {
    accessory: tooltip,
    key: SEGMENTED_CAPACITY_BAR_SEGMENT.CLASSPASS,
    label: (
      <Message
        message={isIntegratedFillBar ? messages.fillBarLabelClasspass : messages.fillBarLabelManual}
        values={{ count: classpassCount }}
      />
    ),
  };

  if (!isIntegratedFillBar) {
    return [classpassItem];
  }

  const directCount = (
    <span className={element('fill-bar-count')}>
      <Message message={messages.fillBarDirectCount} values={fillBarLabelValues} />
    </span>
  );

  return [
    {
      key: SEGMENTED_CAPACITY_BAR_SEGMENT.DIRECT,
      label: <Message message={messages.fillBarLabelDirect} values={{ count: directCount }} />,
    },
    classpassItem,
  ];
}

function StatusBadge({ badge, scheduleId }) {
  const config = STATUS_BADGE_CONFIG[badge.key];

  if (!config) {
    return null;
  }

  const labelValues = { count: badge.count };
  const badgeNode = (
    <Badge icon={config.icon} purpleSoft={config.purpleSoft} size="xs" warningSoft={config.warningSoft}>
      <Message message={config.labelMessage} values={labelValues} />
    </Badge>
  );

  if (!config.tooltipMessage) {
    return badgeNode;
  }

  const tooltipName = `${STATUS_BADGE_TOOLTIP_NAME_PREFIX}-${badge.key}-${scheduleId}`;

  return (
    <>
      <TooltipToggle
        className={element('status-badge-tooltip-toggle')}
        name={tooltipName}
        onMouseDown={event => {
          event.stopPropagation();
        }}>
        {badgeNode}
      </TooltipToggle>
      <TooltipContent hoverable name={tooltipName} place="top">
        <Message message={config.tooltipMessage} values={labelValues} />
      </TooltipContent>
    </>
  );
}

StatusBadge.propTypes = {
  badge: PropTypes.shape({
    count: PropTypes.number,
    key: PropTypes.oneOf(Object.values(SCHEDULE_LIST_V2_STATUS_BADGE)).isRequired,
  }).isRequired,
  scheduleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const ScheduleListV2Item = ({
  actOnOpportunity = () => {},
  canManageSpots = false,
  handleAddSpot = () => {},
  hasMoreSchedules,
  isLastItem,
  isLoadingMoreSchedules,
  isSelected = false,
  loadMoreSchedules,
  registerNode,
  schedule,
  setSelectedSchedule = () => {},
  tab,
  venueId,
}) => {
  const node = useRef(null);

  const {
    duration,
    isCancelled,
    isDisabled,
    reservationsStopped,
    resource,
    scheduleId,
    scheduleName,
    startDate,
    startDateTime = new Date(),
  } = schedule;
  const { resourceName: instructorName } = resource || {};
  const isVisuallyPast = isScheduleListV2InThePast({ schedule });
  const adminBadgeMessageKey = getScheduleListV2AdminBadgeMessage({
    isCancelled,
    isDisabled,
    reservationsStopped,
  });
  const fillBar = getScheduleListV2FillBar(schedule);
  const isIntegratedFillBar = fillBar.variant === SCHEDULE_LIST_V2_FILL_BAR_VARIANT.INTEGRATED;
  const fillBarLabelValues = {
    classpassAllocated: fillBar.classpassAllocated,
    classpassBooked: fillBar.classpassBooked,
    directBooked: fillBar.directBooked,
  };
  const fillBarTooltipName = `${FILL_BAR_TOOLTIP_NAME_PREFIX}-${scheduleId}`;
  const statusRow = getScheduleListV2StatusRow(schedule, {
    canManageSpots,
    isPast: isVisuallyPast,
  });

  useLayoutEffect(() => {
    const { current } = node;

    if (current && scheduleId) {
      registerNode(scheduleId, schedule.isInThePast, current);
    }
  }, [registerNode, schedule.isInThePast, scheduleId]);

  const isInViewport = elementNode => {
    const rect = elementNode.getBoundingClientRect();

    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  useEffect(() => {
    if (node.current && isLastItem && hasMoreSchedules && !isLoadingMoreSchedules && isInViewport(node.current)) {
      loadMoreSchedules();
    }
  }, [isLastItem, isLoadingMoreSchedules]);

  const handleSetAsSelected = event => {
    event.preventDefault();
    setSelectedSchedule({
      focus: 'detail',
      scheduleId,
      startDate,
      tab,
      venueId,
    });
  };

  const handleMetaActionClick = event => {
    event.preventDefault();
    event.stopPropagation();

    if (statusRow.addSpot?.type === SCHEDULE_LIST_V2_ADD_SPOT_TYPE.OPPORTUNITY) {
      actOnOpportunity();
      return;
    }

    handleAddSpot();
  };

  return (
    <div
      ref={node}
      aria-label={`${startDateTime}: ${scheduleName}`}
      className={element('card', {
        'has-status-row': statusRow.show,
        'is-in-the-past': isVisuallyPast,
        'is-selected': isSelected,
        'is-last-item': isLastItem,
      })}
      onClick={handleSetAsSelected}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          handleSetAsSelected(event);
        }
      }}
      role="link"
      tabIndex={0}>
      <div className={element('content-row')}>
        <div className={element('details')}>
          <div className={element('time-section')}>
            <p className={element('start-time')}>
              <Message format="time" value={startDateTime} />
            </p>
            <Message as="p" className={element('duration')} message={messages.durationMinutes} values={{ duration }} />
          </div>
          <div className={element('class-info')}>
            <p className={element('title')} title={scheduleName}>
              {scheduleName}
            </p>
            <p
              className={element('instructor', {
                'is-disabled': isDisabled && !isVisuallyPast,
              })}
              title={instructorName || undefined}>
              {instructorName || <Message message={messages.noInstructor} />}
            </p>
          </div>
        </div>
        <When predicate={!adminBadgeMessageKey}>
          <SegmentedCapacityBar
            className={element('fill-bar')}
            legendItems={getFillBarLegendItems({
              fillBarLabelValues,
              isIntegratedFillBar,
              tooltip: (
                <FillBarClasspassTooltip
                  name={fillBarTooltipName}
                  show={fillBar.showClasspassFullTooltip && !isVisuallyPast}
                />
              ),
            })}
            muted={isVisuallyPast}
            segments={fillBar.segments}
          />
        </When>
        <When predicate={Boolean(adminBadgeMessageKey)}>
          <div className={element('admin-badge')}>
            <Message message={messages[adminBadgeMessageKey]} />
          </div>
        </When>
      </div>
      <When predicate={statusRow.show}>
        <div className={element('status-row')}>
          <div className={element('status-badges')}>
            {statusRow.badges.map(badge => (
              <StatusBadge badge={badge} key={badge.key} scheduleId={scheduleId} />
            ))}
          </div>
          <When predicate={Boolean(statusRow.addSpot)}>
            <button className={element('add-spot-link')} onClick={handleMetaActionClick} type="button">
              <Message
                message={
                  statusRow.addSpot?.type === SCHEDULE_LIST_V2_ADD_SPOT_TYPE.OPPORTUNITY
                    ? messages.addSpotsLink
                    : messages.addSpotLink
                }
                values={{ count: statusRow.addSpot?.spotsToAdd }}
              />
            </button>
          </When>
        </div>
      </When>
    </div>
  );
};

ScheduleListV2Item.propTypes = {
  actOnOpportunity: PropTypes.func,
  canManageSpots: PropTypes.bool,
  handleAddSpot: PropTypes.func,
  hasMoreSchedules: PropTypes.bool,
  isLastItem: PropTypes.bool,
  isLoadingMoreSchedules: PropTypes.bool,
  isSelected: PropTypes.bool,
  loadMoreSchedules: PropTypes.func,
  registerNode: PropTypes.func,
  schedule: PropTypes.shape(schedulePropTypes),
  setSelectedSchedule: PropTypes.func,
  tab: PropTypes.string,
  venueId: PropTypes.number,
};

export default ScheduleListV2Item;
